import type { AxiosResponse } from "axios";

/**
 * The error thrown for a response whose status is outside the success range.
 * It keeps the status and any message the server gave, so that a caller can
 * tell a request the server refused ({@link isRefusal}) from one that failed.
 */
export class UnsuccessfulResponseError extends Error {
  // `name` is left as the inherited "Error" on purpose: error tracking takes the
  // error type from it, so a new name would split these failures from their
  // existing issues.
  readonly status: number;
  readonly serverMessage: string | undefined;

  constructor(status: number, serverMessage: string | undefined) {
    super(serverMessage ?? "Something went wrong. Please try again.");
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

// The request is invalid (400) or conflicts with the current state (409). This
// is an allow-list because other 4xx answers concern the session, permissions,
// visibility or load rather than the request, and their message need not be
// written for the user.
const REFUSAL_STATUSES = new Set([400, 409]);

/**
 * Whether the server judged the request itself and refused it, giving a
 * message. That message is then meant for the user, and making the same request
 * again will not change the answer. A refusal whose body gives no `message`
 * string, or an empty one, does not count: it leaves nothing from the server to
 * show.
 */
export function isRefusal(error: unknown): error is UnsuccessfulResponseError {
  return (
    error instanceof UnsuccessfulResponseError &&
    REFUSAL_STATUSES.has(error.status) &&
    error.serverMessage !== undefined &&
    error.serverMessage !== ""
  );
}

/**
 * Asserts that an Axios response indicates success (status 200-399).
 *
 * The shared `apiAxios` instance is configured with
 * `validateStatus: (status) => status >= 200 && status < 500`, meaning 4xx
 * responses do not throw by default. Call this helper after any mutation
 * request to ensure a failed or otherwise unexpected response is surfaced as a
 * thrown error that callers can handle.
 */
export function assertOkResponse<T>(
  response: AxiosResponse<T>,
): AxiosResponse<T> {
  if (response.status < 200 || response.status >= 400) {
    const data = response.data as { message?: unknown } | null | undefined;
    const message = data?.message;
    throw new UnsuccessfulResponseError(
      response.status,
      typeof message === "string" ? message : undefined,
    );
  }
  return response;
}
